import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CardSkeletons } from '@todo/web-ui/components/card-skeletons';
import { EmptyState } from '@todo/web-ui/components/empty-state';
import { EventGrid } from '@todo/web-ui/components/event-grid';
import { Intro, Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { CardGrid, Section } from '@todo/web-ui/components/section';
import { seedScript } from '@todo/web-ui/components/seed-script';
import { RelatedGuides, relatedGuidesSeed } from '@todo/web-ui/components/related-guides';
import { TopicLinks } from '@todo/web-ui/components/collection-guide';
import { HUB_GUIDES, SEE_ALSO_PAGES } from '@/lib/collections';
import { EVENTS_INDEX_SEED_KEY } from '@/lib/prefetch';
import { useEventCards } from '@/lib/use-event-cards';
import { localizedTitle } from '@todo/core/i18n/event-content';
import { useLocale } from '@todo/core/i18n/use-locale';
import { absoluteUrl, breadcrumbJsonLd, eventsCrumb, itemListJsonLd, seoTitle } from '@todo/core/seo';

import css from './events.module.css';

/** Date windows, in display order: getDateRange ids (Dubai days), 'any' = the next 180 days. */
const WHENS = ['any', 'today', 'weekend', 'week', 'month'] as const;
type When = (typeof WHENS)[number];

/** Category filter options, in display order; only those with shows in the list are offered. */
const CATEGORIES = ['gastronomy', 'parties', 'concerts', 'theatre', 'family', 'sport', 'festivals', 'exhibitions', 'cinema', 'other'];

/** Shows in the ItemList JSON-LD — the first page of cards, not the whole list. */
const ITEM_LIST_MAX = 24;

// Every show in Dubai in the next 180 days (Platinumlist), recommended first —
// popularity, exclusive events ranked lower (sync-platinumlist's list_rank) —
// with filters by date window and category. The static HTML is the unfiltered
// seed; a category filters it in the browser, a date window asks
// event_shows_filtered (a recurring show is matched on any of its dates in the
// window, which the seed — each show's soonest date only — can't tell).
export default function EventsIndex() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [when, setWhen] = useState<When>('any');
  const [category, setCategory] = useState('all');
  const { events: windowed, loading, error } = useEventCards(EVENTS_INDEX_SEED_KEY, 'any', when);
  // The unfiltered list: the seed (what the static HTML and its JSON-LD are
  // built from), or — after a client-side navigation — the first fetch; kept
  // while a date window is on.
  const [seed, setSeed] = useState(windowed);
  useEffect(() => {
    if (when === 'any' && !loading) setSeed(windowed);
  }, [when, loading, windowed]);

  // Category options from the whole list, so the row doesn't jump while a
  // date window loads (a window without one shows "nothing matches").
  const categories = useMemo(() => CATEGORIES.filter((c) => seed.some((e) => (e.category_id ?? 'other') === c)), [seed]);
  // Memoized: EventGrid starts over from its first page whenever this list changes.
  const shown = useMemo(
    () => (category === 'all' ? windowed : windowed.filter((e) => (e.category_id ?? 'other') === category)),
    [windowed, category],
  );
  const filtered = when !== 'any' || category !== 'all';

  return (
    <Page>
      <PageHead
        title={seoTitle(t('events.metaTitle'))}
        description={t('events.metaDescription')}
        path="events"
        // Indexed as the unfiltered list (its canonical); the filters are client-side only.
        indexable={seed.length > 0}>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd(locale, [{ name: eventsCrumb(locale).name }]))}</script>
        {seed.length ? (
          <script type="application/ld+json">
            {JSON.stringify(
              itemListJsonLd(
                seed.slice(0, ITEM_LIST_MAX).map((e) => ({
                  name: localizedTitle(e, locale),
                  url: absoluteUrl(locale, `events/${e.show_slug}`),
                })),
                absoluteUrl(locale, 'events'),
              ),
            )}
          </script>
        ) : null}
        {seedScript(EVENTS_INDEX_SEED_KEY)}
        {relatedGuidesSeed('events', locale)}
      </PageHead>

      <Section id="events-title" level={1} title={t('events.title')}>
        <Intro>{t('events.intro')}</Intro>
        <TopicLinks collections={[]} pages={SEE_ALSO_PAGES.events} />

        {seed.length ? (
          <>
            <div className={css.filters}>
              <div className={css.group} role="group" aria-labelledby="events-when">
                <span id="events-when" className={css.legend}>
                  {t('events.filters.when')}
                </span>
                {WHENS.map((w) => (
                  <button key={w} type="button" className={css.chip} aria-pressed={when === w} onClick={() => setWhen(w)}>
                    {t(`events.filters.whens.${w}`)}
                  </button>
                ))}
              </div>
              {categories.length > 1 ? (
                <div className={css.group} role="group" aria-labelledby="events-category">
                  <span id="events-category" className={css.legend}>
                    {t('events.filters.category')}
                  </span>
                  {['all', ...categories].map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={css.chip}
                      aria-pressed={category === c}
                      onClick={() => setCategory(c)}>
                      {c === 'all' ? t('events.filters.all') : t(`events.categories.${c}`)}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            {filtered ? (
              <p className={css.status} aria-live="polite">
                {error
                  ? t('events.filters.error')
                  : loading
                    ? t('events.filters.loading')
                    : t('events.filters.count', { count: shown.length })}
              </p>
            ) : null}
            {loading ? (
              <CardGrid>
                <CardSkeletons />
              </CardGrid>
            ) : shown.length ? (
              <EventGrid events={shown} />
            ) : error ? null : (
              <p className={css.status}>{t('events.filters.none')}</p>
            )}
          </>
        ) : loading ? (
          <CardGrid>
            <CardSkeletons />
          </CardGrid>
        ) : (
          <EmptyState text={t('events.empty')} exclude="events" />
        )}
      </Section>

      <RelatedGuides page="events" guides={HUB_GUIDES.events} />
    </Page>
  );
}
